<template>
  <a-swiper
    ref="swiper"
    class="carousel__wrapper"
    :class="carouselDynamicClass"
    :swipe-list="logos"
    :custom-options="swiperOptions"
    :display-arrows="false"
    :swiper-pagination-custom-class="swiperPaginationCustomClass"
    no-margins
    pagination
    @slider-move="onUserInteraction"
    @pagination-click="onUserInteraction"
  >
    <div
      class="carousel__slide-wrapper"
      slot-scope="{ slide, index }"
      @mouseenter="enlargeSlide(index)"
      @mouseleave="resetSlide(index)"
    >
      <a-responsive
        :aspect-ratio="$options.consts.ASPECT_RATIO.CAROUSEL_SLIDE"
        class="carousel__slide-responsive-wrapper"
      >
        <div class="carousel__icon-wrapper">
          <a-image
            object-fit="contain"
            :aspect-ratio="$options.consts.ASPECT_RATIO.CAROUSEL_SLIDE"
            :src="slide.url"
            :alt="slide.alt"
            class="carousel__icon"
            @drag.native.prevent
          />
        </div>
      </a-responsive>
    </div>

    <div
      class="swiper-pagination swiper-pagination-bullets"
      :class="swiperPaginationCustomClass"
      slot="bullets"
    />
  </a-swiper>
</template>

<script>
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { ASPECT_RATIO } from 'enums/aspectRatios'
import ASwiper from 'shared/ASwiper/index.vue'
import AResponsive from 'shared/AResponsive/index.vue'
import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import { SWIPER_SLIDE_CLASS } from 'shared/ASwiper/enums'

const DEFAULT_AUTOPLAY_DELAY = 2000
const RESET_SLIDE_TIMEOUT = 700
const SWIPER_ENLARGED_SLIDE_CLASS = 'swiper-slide-enlarged'

export default {
  name: 'HomepageSwiperWithLogos',
  components: { AResponsive, ASwiper },
  mixins: [hydrationHelpers, mixins.refDimensions],
  props: {
    logos: propValidator([PROP_TYPES.ARRAY]),

    /**
     * If it's the same between multiple instances - it will glitch
     */
    swiperPaginationCustomClass: propValidator([PROP_TYPES.STRING]),
    enlargeSlideOnInteraction: propValidator(
      [PROP_TYPES.BOOLEAN],
      false,
      false
    ),
    autoplayDelay: propValidator(
      [PROP_TYPES.NUMBER],
      false,
      DEFAULT_AUTOPLAY_DELAY
    )
  },
  consts: {
    ASPECT_RATIO
  },
  data() {
    return {
      enlargedSlideIndex: null,
      resetTimeoutId: null,
      wasInteraction: false
    }
  },
  computed: {
    carouselDynamicClass() {
      return {
        enlargeable: this.enlargeSlideOnInteraction
      }
    },
    basicSwiperOptions() {
      return {
        slidesPerView: 6.15,
        spaceBetween: '1.5%',
        breakpoints: {
          [this.$breakpoint.mobile]: {
            spaceBetween: 14,
            slidesPerView: 2.8
          },
          [this.$breakpoint.desktopSm]: {
            spaceBetween: 14,
            slidesPerView: 5.37
          }
        },
        slidesPerGroup: 1,
        autoplay: {
          delay: this.autoplayDelay
        }
      }
    },
    swiperOptions() {
      const pagination = {
        pagination: {
          el: `.${this.swiperPaginationCustomClass}`,
          clickable: true,
          renderBullet(index, className) {
            return `<span class="${className} swiper-pagination-bullet-custom-lg"></span>`
          }
        }
      }

      return {
        ...this.basicSwiperOptions,
        ...(this.$_hydrationHelpers_isLayoutMobile ? {} : pagination)
      }
    }
  },
  methods: {
    onUserInteraction() {
      this.wasInteraction = true
    },
    resetSlide() {
      if (!this.enlargeSlideOnInteraction) return

      this.resetTimeoutId = setTimeout(() => {
        this.removeActiveEnlargedSlideClass()

        if (!this.wasInteraction) {
          this.$refs.swiper.startAutoplay()
        }
      }, RESET_SLIDE_TIMEOUT)
    },
    removeActiveEnlargedSlideClass() {
      const targetEl = this.$el.querySelector(`.${SWIPER_ENLARGED_SLIDE_CLASS}`)

      if (!targetEl) return

      targetEl.classList.remove(SWIPER_ENLARGED_SLIDE_CLASS)
    },
    enlargeSlide(index) {
      if (!this.enlargeSlideOnInteraction) return

      clearTimeout(this.resetTimeoutId)

      /**
       * We manipulate DOM directly to avoid "infinite update loop in a component
       * render function" error
       */
      this.removeActiveEnlargedSlideClass()

      this.$el
        .querySelectorAll(`.${SWIPER_SLIDE_CLASS}`)
        [index].classList.add(SWIPER_ENLARGED_SLIDE_CLASS)

      this.$refs.swiper.update()
      this.$refs.swiper.stopAutoplay()
    }
  }
}
</script>

<style lang="scss" scoped>
$slide-base-width: 218px;
$slide-base-height: 109px;

$slide-enlarged-width: 324px;
$slide-enlarged-height: 162px;

$slide-width-diff: $slide-enlarged-width - $slide-base-width;
$slide-height-diff: $slide-enlarged-height - $slide-base-height;

// How width changed in %
$slide-width-diff-percentage: percentage($slide-width-diff / $slide-base-width);

// How width changed in % (relative to enlarged width)
$slide-width-diff-percentage-to-enlarged: percentage(
  $slide-width-diff / $slide-enlarged-width
);

// How height changed in %
$slide-height-diff-percentage: percentage(
  $slide-height-diff / $slide-base-height
);

// How height changed in relation to width in %
$slide-height-diff-by-width-percentage: percentage(
  $slide-height-diff / $slide-base-width
);

$slide-shadow-offset-lg: 20px;
$slide-shadow-offset-md: 10px;

@function getSlideWrapperMargin($shadowOffset) {
  @return calc(#{$slide-height-diff-by-width-percentage / 2} + #{$shadowOffset})
    0;
}

@function getSlideBoxShadow($shadowOffset) {
  @return 0 0 #{$shadowOffset} 0 rgba(0, 0, 0, 0.16);
}

.carousel__wrapper {
  .carousel__slide-wrapper {
    width: 100%;
  }

  .carousel__slide-responsive-wrapper {
    position: relative;

    .carousel__icon-wrapper {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      transition: all 0.5s ease;
      transform: translateX(0);
      overflow: hidden;
      background: white;
      opacity: 1;
      display: flex;
      align-items: center;

      $padding: (
        $above-desktop-xl: 20px,
        $mobile: 10px
      );

      $border-radius: (
        $above-desktop-xl: 20px,
        $desktop-sm: 14px
      );

      $box-shadow: (
        $above-desktop-xl: getSlideBoxShadow($slide-shadow-offset-md)
      );

      @include setStyleByBreakpoint('padding', $padding);
      @include setStyleByBreakpoint('border-radius', $border-radius);
      @include setStyleByBreakpoint('box-shadow', $box-shadow);
    }

    .carousel__icon {
      width: 100%;
    }
  }

  &.enlargeable {
    .carousel__slide-wrapper {
      $margin: (
        $above-desktop-xl: getSlideWrapperMargin($slide-shadow-offset-lg),
        $desktop-sm: getSlideWrapperMargin($slide-shadow-offset-md)
      );

      @include setStyleByBreakpoint('margin', $margin);
    }

    .carousel__icon-wrapper {
      opacity: 0.3;
      background: #dadada;
      box-shadow: none;
    }

    /deep/ .swiper-container {
      padding: 0;
    }

    /deep/ .swiper-pagination-bullet-custom-lg {
      background: #000;
      opacity: 0.2;

      &.swiper-pagination-bullet-active {
        background: $c--main;
        opacity: 1;
      }
    }
  }

  /deep/ .swiper-slide-enlarged {
    z-index: 1;

    .carousel__icon-wrapper {
      left: -#{$slide-width-diff-percentage / 2};
      right: -#{$slide-width-diff-percentage / 2};
      bottom: -#{$slide-height-diff-percentage / 2};
      top: -#{$slide-height-diff-percentage / 2};

      opacity: 1;
      background: white;

      $box-shadow: (
        $above-desktop-xl: getSlideBoxShadow($slide-shadow-offset-lg),
        $desktop-sm: getSlideBoxShadow($slide-shadow-offset-md)
      );

      @include setStyleByBreakpoint('box-shadow', $box-shadow);
    }

    &:last-child .carousel__icon-wrapper {
      $left: (
        $above-desktop-xl:
          calc(-#{$slide-width-diff-percentage} - #{$slide-shadow-offset-lg}),
        $desktop-sm:
          calc(-#{$slide-width-diff-percentage} - #{$slide-shadow-offset-md})
      );

      $right: (
        $above-desktop-xl: $slide-shadow-offset-lg,
        $desktop-sm: $slide-shadow-offset-md
      );

      @include setStyleByBreakpoint('left', $left);
      @include setStyleByBreakpoint('right', $right);
    }

    &.swiper-slide-active {
      .carousel__icon-wrapper {
        $left: (
          $above-desktop-xl: $slide-shadow-offset-lg,
          $desktop-sm: $slide-shadow-offset-md
        );

        $right: (
          $above-desktop-xl:
            calc(-#{$slide-width-diff-percentage} - #{$slide-shadow-offset-lg}),
          $desktop-sm:
            calc(-#{$slide-width-diff-percentage} - #{$slide-shadow-offset-md})
        );

        @include setStyleByBreakpoint('left', $left);
        @include setStyleByBreakpoint('right', $right);
      }
    }
  }

  /deep/ .swiper-container {
    padding: $slide-shadow-offset-md $slide-shadow-offset-md / 2;
  }
}
</style>

<style lang="scss">
.carousel__wrapper {
  .swiper-buttons {
    display: flex;

    $margin-top: (
      $above-desktop-xl: 24px,
      $desktop-sm: 14px,
      $mobile: 0
    );

    @include setStyleByBreakpoint('margin-top', $margin-top);
  }

  &.enlargeable {
    .swiper-buttons {
      $margin-top: (
        $desktop-sm: 2px
      );

      @include setStyleByBreakpoint('margin-top', $margin-top);
    }
  }

  .swiper-pagination-bullet-custom-lg {
    width: 12px;
    height: 12px;
    margin: 0 7px;
    text-align: center;
    line-height: 10px;
    font-size: 12px;
    background: #fff;
    opacity: 1;
  }

  .swiper-pagination-bullet-custom-lg.swiper-pagination-bullet-active {
    background: $c--main;
  }

  .swiper-pagination {
    @include mobile {
      display: none;
    }
  }
}
</style>
